import { DragGrip } from "../Icons/Interface";
import { JobCardData } from "../../../DataModels/DataModels";
import { formatTime } from "../../../Utilities/FormatTime";
import { JobCardMenu } from "../Menu/JobCardMenu";
import { useMenuItems } from "../../../services/api-client/useMenuItems";
import { useState } from "react";

// Chevron SVG Component
const ChevronBadge: React.FC<{
	timeText: string;
	isError: boolean;
	showActive: boolean;
}> = ({ timeText, isError, showActive }) => {
	return (
		<div
			className={`relative ${isError ? "text-error-active" : showActive ? "text-primary-active" : "text-primary-active"}`}
		>
			<svg
				width="48"
				height="48"
				viewBox="0 0 48 32"
				className="absolute -top-3 -right-1"
			>
				<path
					d="M36 0V26.0303C36 27.2333 35.3777 28.2971 34.4355 28.9248L20.9951 37.7119C20.3907 38.0954 19.6269 38.0955 19.0225 37.7119L5.56445 28.9248C4.62227 28.2971 4 27.2333 4 26.0303L4.01562 5.88477H4V0H36Z"
					fill="currentColor"
					stroke="none"
				/>
				<text
					x="20"
					y="22.5"
					textAnchor="middle"
					fontSize="14"
					fill="text-primary-background"
					fontFamily="sans-serif"
					fontWeight="bold"
				>
					{timeText}
				</text>
			</svg>
		</div>
	);
};

interface JobCardProps {
	data: JobCardData;
	isActive?: boolean;
	isArchive?: boolean;
	isDemo?: boolean;
}

const JobCard: React.FC<JobCardProps> = ({
	data,
	isActive = false,
	isArchive = false,
	isDemo = false
}) => {
	// Always call hooks unconditionally
	const menuSource = useMenuItems();
	const [active, setActive] = useState(isActive);
	const timeAgo = formatTime(new Date(data.creationTimestamp));
	const displayTime = data.isError ? `${timeAgo}!` : timeAgo;

	// Handle menu button click to prevent event bubbling
	const cancelMenuClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // This prevents the event from bubbling up to the card's onClick
	};

	// Handle card click with pending state check
	const handleCardClick = () => {
		if (isDemo || data.isPending) {
			return; // Disable clicking when demo mode or pending
		}
		menuSource?.menuItems[0]?.actionFunc?.(data);
	};

	return (
		<div
			className={`flex bg-primary-background rounded-2xl p-0 max-w-84 border transition-all duration-200 ${
				data.isPending
					? "cursor-not-allowed animate-pulse"
					: isDemo
						? "cursor-default"
						: "cursor-pointer"
			} ${
				data.isError
					? "border-error-active"
					: active
						? "border-primary"
						: "border-primary"
			}`}
			onMouseEnter={() => !data.isPending && !isDemo && setActive(true)}
			onMouseLeave={() => setActive(false)}
			onClick={handleCardClick}
		>
			{/* Grab Handle  left-2 top-1/2 */}
			<div
				className={`text-primary flex items-center ${active ? "invisible" : "invisible"}`}
			>
				{DragGrip()}
			</div>

			{/* Main Content */}
			<div className="mt-4 mb-4 flex-1">
				<div className="text-primary font-medium text-sm mb-1 truncate max-w-44">
					{data.company}
				</div>
				<div className="text-primary text-sm truncate max-w-44">
					{data.title}
				</div>
			</div>

			<div className="mt-1 mr-4">
				{/* Time Badge */}
				<div className="text-primary">
					<ChevronBadge
						timeText={displayTime}
						isError={data.isError}
						showActive={active}
					/>
				</div>

				{/* Action Menu Button */}
				{!isArchive && !isDemo && !data.isPending && (
					<div
						className={`relative flex items-center justify-center ${active ? "" : "invisible"}}`}
						onClick={cancelMenuClick}
					>
						<div className="text-primary hover:text-primary-inactive mt-10 mr-2 -top-10 ">
							<JobCardMenu actionList={menuSource.menuItems} jobKey={data} />
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default JobCard;
